import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Share,
  ArrowBack,
  People,
  MoreVert,
  PictureAsPdf,
  Image as ImageIcon,
  Delete,
  Settings,
  Add,
  CameraAlt,
  PhotoLibrary,
  CloudUpload,
} from '@mui/icons-material';
import { AppLayout } from '@/components/layout';
import {
  NotebookCanvas,
  NotebookToolbar,
  PageNavigation,
  TemplatePicker,
  FullscreenNotebook,
} from '@/components/notebooks';
import type { NotebookCanvasRef, ToolType } from '@/components/notebooks/NotebookCanvas';
import { NotebookShareDialog } from '@/components/dialogs';
import { useNotebook } from '@/hooks';
import { useCamera } from '@/hooks/useCamera';
import { brandColors } from '@/theme/colors';
import type { PageTemplate as PageTemplateType } from '@/types/notebook';
import { TEMPLATE_NAMES } from '@/types/notebook';
import { PlatformUtils } from '@/utils/platform';
import { storageService } from '@/services/firebase/storage';
import { useAuth } from '@/contexts/AuthContext';
import type { CapturedImage } from '@/services/camera/cameraService';

export const NotebookPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    notebook,
    loading,
    error,
    canEdit,
    isOwner,
    pages,
    currentPage,
    currentPageIndex,
    pageCount,
    addPageAfterCurrent,
    updateCurrentPage,
    saveCurrentPage,
    deleteCurrentPage,
    goToPage,
  } = useNotebook(id || '');

  const canvasRef = useRef<NotebookCanvasRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Camera hook for native platforms
  const { takePhoto, pickFromGallery, loading: cameraLoading, error: cameraError } = useCamera();
  const isNative = PlatformUtils.isNative();

  // Tool state
  const [tool, setTool] = useState<ToolType>('pen');
  const [penColor, setPenColor] = useState('#000000');
  const [penWidth, setPenWidth] = useState(2);
  const [zoom, setZoom] = useState(1);

  // Dialog states
  const [shareOpen, setShareOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deletePageConfirmOpen, setDeletePageConfirmOpen] = useState(false);
  const [deleteSelectedConfirmOpen, setDeleteSelectedConfirmOpen] = useState(false);
  const [clearPageConfirmOpen, setClearPageConfirmOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [imageError, setImageError] = useState<string | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle canvas changes (debounced save - no thumbnail for responsiveness)
  const handleCanvasChange = useCallback((data: string) => {
    if (!canEdit) return;

    // Only save canvas data - thumbnails are expensive and generated on page change
    updateCurrentPage({
      canvasData: data,
    });
  }, [canEdit, updateCurrentPage]);

  // Poll for selection changes (to enable/disable delete button)
  useEffect(() => {
    if (!canEdit) return;

    const interval = setInterval(() => {
      const hasItems = canvasRef.current?.hasSelection() ?? false;
      setHasSelection(hasItems);
    }, 200);

    return () => clearInterval(interval);
  }, [canEdit]);

  // Handle keyboard shortcuts (Delete and Escape for fullscreen)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape exits fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        return;
      }

      if (!canEdit) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only handle if not typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
          return;
        }

        if (canvasRef.current?.hasSelection()) {
          e.preventDefault();
          setDeleteSelectedConfirmOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canEdit, isFullscreen]);

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
    // Reset zoom to 100% when entering fullscreen for tablet-like experience
    if (!isFullscreen) {
      setZoom(1);
    }
  };

  // Handle undo/redo
  const handleUndo = () => canvasRef.current?.undo();
  const handleRedo = () => canvasRef.current?.redo();

  // Handle clear page (with confirmation)
  const handleClearClick = () => {
    if (canEdit) {
      setClearPageConfirmOpen(true);
    }
  };

  const handleClearConfirm = () => {
    if (canvasRef.current && canEdit) {
      canvasRef.current.clear();
    }
    setClearPageConfirmOpen(false);
  };

  // Handle delete selected (with confirmation)
  const handleDeleteSelectedClick = () => {
    if (canvasRef.current?.hasSelection()) {
      setDeleteSelectedConfirmOpen(true);
    }
  };

  const handleDeleteSelectedConfirm = () => {
    if (canvasRef.current && canEdit) {
      canvasRef.current.deleteSelected();
    }
    setDeleteSelectedConfirmOpen(false);
  };

  // Handle page change - save current canvas first
  const handlePageChange = async (newIndex: number) => {
    if (canvasRef.current && canEdit && currentPage) {
      const data = canvasRef.current.getCanvasData();
      const thumbnail = canvasRef.current.getThumbnail();
      await saveCurrentPage({ canvasData: data, thumbnail });
    }
    goToPage(newIndex);
  };

  // Handle add page
  const handleAddPage = async () => {
    if (canvasRef.current && canEdit && currentPage) {
      const data = canvasRef.current.getCanvasData();
      const thumbnail = canvasRef.current.getThumbnail();
      await saveCurrentPage({ canvasData: data, thumbnail });
    }
    await addPageAfterCurrent();
  };

  // Handle delete page
  const handleDeletePage = async () => {
    setDeletePageConfirmOpen(false);
    await deleteCurrentPage();
  };

  // Handle template change for current page
  const handleTemplateChange = async (template: PageTemplateType) => {
    if (currentPage && canEdit) {
      await updateCurrentPage({ template });
    }
    setTemplateDialogOpen(false);
  };

  // Image insertion handlers
  const handleInsertImage = () => {
    setImageError(null);
    setImagePickerOpen(true);
  };

  const handleImageUpload = async (imageUrl: string) => {
    if (!canvasRef.current) return;
    await canvasRef.current.addImage(imageUrl);
    setImagePickerOpen(false);
    setImageUploading(false);
    setImageUploadProgress(0);
  };

  const handleCameraCapture = async (capturedImage: CapturedImage | null) => {
    if (!capturedImage || !id || !user) return;

    // Validate file size (max 5MB)
    if (capturedImage.file.size > 5 * 1024 * 1024) {
      setImageError('Image size must be less than 5MB');
      return;
    }

    setImageError(null);
    setImageUploading(true);
    setImageUploadProgress(0);

    try {
      // Generate storage path for notebook images
      const path = `notebooks/${id}/images/${Date.now()}_camera.${capturedImage.format}`;

      // Upload file
      const imageUrl = await storageService.uploadFile(path, capturedImage.file, (progress) => {
        setImageUploadProgress(progress);
      });

      // Add image to canvas
      await handleImageUpload(imageUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setImageError('Failed to upload image. Please try again.');
      setImageUploading(false);
      setImageUploadProgress(0);
    }
  };

  const handleTakePhoto = async () => {
    const image = await takePhoto();
    await handleCameraCapture(image);
  };

  const handlePickFromGallery = async () => {
    const image = await pickFromGallery();
    await handleCameraCapture(image);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setImageError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image size must be less than 5MB');
      return;
    }

    setImageError(null);
    setImageUploading(true);
    setImageUploadProgress(0);

    try {
      // Generate storage path
      const path = `notebooks/${id}/images/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;

      // Upload file
      const imageUrl = await storageService.uploadFile(path, file, (progress) => {
        setImageUploadProgress(progress);
      });

      // Add image to canvas
      await handleImageUpload(imageUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setImageError('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
      setImageUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Export as PDF
  const handleExportPDF = async () => {
    if (!notebook) return;

    // TODO: Implement PDF export using jsPDF
    // For now, show a placeholder alert
    alert('PDF export coming soon!');
    setMenuAnchor(null);
  };

  // Export as image
  const handleExportImage = () => {
    if (!canvasRef.current) return;

    const dataUrl = canvasRef.current.getThumbnail();
    if (dataUrl) {
      const link = document.createElement('a');
      link.download = `${notebook?.name || 'notebook'}-page-${currentPageIndex + 1}.png`;
      link.href = dataUrl.replace(/^data:image\/png/, 'data:application/octet-stream');
      link.click();
    }
    setMenuAnchor(null);
  };

  if (loading) {
    return (
      <AppLayout showBackToDashboard title="Loading...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error || !notebook) {
    return (
      <AppLayout showBackToDashboard backToSection="notebooks" title="Error">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || 'Notebook not found'}
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard', { state: { section: 'notebooks' } })}>
            Back to Dashboard
          </Button>
        </Container>
      </AppLayout>
    );
  }

  // Fullscreen mode - tablet-like experience (Remarkable/A4 paper feel)
  if (isFullscreen && currentPage) {
    return (
      <FullscreenNotebook
        canvasRef={canvasRef}
        template={currentPage.template}
        paperSize={notebook.paperSize}
        initialData={currentPage.canvasData}
        readOnly={!canEdit}
        tool={tool}
        onToolChange={setTool}
        penColor={penColor}
        onColorChange={setPenColor}
        penWidth={penWidth}
        onWidthChange={setPenWidth}
        onCanvasChange={handleCanvasChange}
        onExit={() => setIsFullscreen(false)}
        currentPageIndex={currentPageIndex}
        pageCount={pageCount}
        onPageChange={handlePageChange}
        onAddPage={handleAddPage}
        canEdit={canEdit}
      />
    );
  }

  // Normal mode
  return (
    <AppLayout showBackToDashboard backToSection="notebooks" title={notebook.name}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.purple }}>
              {notebook.name}
            </Typography>
            {notebook.sharedWith.length > 0 && (
              <Chip
                icon={<People sx={{ fontSize: 14 }} />}
                label={`Shared with ${notebook.sharedWith.length}`}
                size="small"
                sx={{
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                }}
              />
            )}
            {!canEdit && (
              <Chip label="View Only" size="small" color="warning" variant="outlined" />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isOwner && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Share />}
                onClick={() => setShareOpen(true)}
                sx={{
                  borderColor: brandColors.purple,
                  color: brandColors.purple,
                  '&:hover': {
                    borderColor: brandColors.purpleDeep,
                    bgcolor: alpha(brandColors.purple, 0.05),
                  },
                }}
              >
                Share
              </Button>
            )}
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        {/* Toolbar */}
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          <NotebookToolbar
            tool={tool}
            onToolChange={setTool}
            penColor={penColor}
            onColorChange={setPenColor}
            penWidth={penWidth}
            onWidthChange={setPenWidth}
            zoom={zoom}
            onZoomChange={setZoom}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onClear={handleClearClick}
            onDeleteSelected={handleDeleteSelectedClick}
            hasSelection={hasSelection}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
            onInsertImage={canEdit ? handleInsertImage : undefined}
            readOnly={!canEdit}
          />
        </Box>

        {/* Main content area */}
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {/* Page Navigation Sidebar */}
          <PageNavigation
            pages={pages}
            currentPageIndex={currentPageIndex}
            onPageSelect={handlePageChange}
            onAddPage={handleAddPage}
            canEdit={canEdit}
          />

          {/* Canvas Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.200',
              overflow: 'auto',
              p: 2,
            }}
          >
            {currentPage && (
              <Box
                sx={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center',
                  transition: 'transform 0.2s',
                }}
              >
                <NotebookCanvas
                  ref={canvasRef}
                  template={currentPage.template}
                  paperSize={notebook.paperSize}
                  initialData={currentPage.canvasData}
                  readOnly={!canEdit}
                  tool={tool}
                  penColor={penColor}
                  penWidth={penWidth}
                  onCanvasChange={handleCanvasChange}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Page info bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {currentPageIndex + 1} of {pageCount}
          </Typography>
          {currentPage && (
            <>
              <Divider orientation="vertical" flexItem />
              <Chip
                label={TEMPLATE_NAMES[currentPage.template]}
                size="small"
                variant="outlined"
                onClick={() => canEdit && setTemplateDialogOpen(true)}
                sx={{ cursor: canEdit ? 'pointer' : 'default' }}
              />
            </>
          )}
          {canEdit && (
            <>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Add new page">
                <IconButton size="small" onClick={handleAddPage}>
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
              {pageCount > 1 && (
                <Tooltip title="Delete this page">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeletePageConfirmOpen(true)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Menus and Dialogs */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleExportPDF}>
          <PictureAsPdf sx={{ mr: 1 }} fontSize="small" />
          Export as PDF
        </MenuItem>
        <MenuItem onClick={handleExportImage}>
          <ImageIcon sx={{ mr: 1 }} fontSize="small" />
          Export Page as Image
        </MenuItem>
        {canEdit && (
          <>
            <Divider />
            <MenuItem onClick={() => { setTemplateDialogOpen(true); setMenuAnchor(null); }}>
              <Settings sx={{ mr: 1 }} fontSize="small" />
              Change Page Template
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Share Dialog */}
      {notebook && (
        <NotebookShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          notebook={notebook}
        />
      )}

      {/* Delete Page Confirmation */}
      <Dialog open={deletePageConfirmOpen} onClose={() => setDeletePageConfirmOpen(false)}>
        <DialogTitle>Delete Page?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete page {currentPageIndex + 1}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Selected Items Confirmation */}
      <Dialog open={deleteSelectedConfirmOpen} onClose={() => setDeleteSelectedConfirmOpen(false)}>
        <DialogTitle>Delete Selected Items?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the selected items? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSelectedConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSelectedConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Page Confirmation */}
      <Dialog open={clearPageConfirmOpen} onClose={() => setClearPageConfirmOpen(false)}>
        <DialogTitle>Clear Page?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all content from this page? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearPageConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleClearConfirm} color="warning" variant="contained">
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Picker Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Page Template</DialogTitle>
        <DialogContent>
          <TemplatePicker
            selectedTemplate={currentPage?.template || 'blank'}
            onTemplateSelect={handleTemplateChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Image Picker Dialog */}
      <Dialog
        open={imagePickerOpen}
        onClose={() => !imageUploading && setImagePickerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          {(imageError || cameraError) && (
            <Alert severity="error" onClose={() => setImageError(null)} sx={{ mb: 2 }}>
              {imageError || cameraError?.message}
            </Alert>
          )}

          {imageUploading || cameraLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {cameraLoading ? 'Processing...' : 'Uploading...'}
              </Typography>
              {imageUploading && (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={imageUploadProgress}
                    sx={{ width: '100%', maxWidth: 200, mx: 'auto' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(imageUploadProgress)}%
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Stack spacing={2} sx={{ py: 2 }}>
              {/* Camera options - only on native platforms */}
              {isNative && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CameraAlt />}
                    onClick={handleTakePhoto}
                    fullWidth
                    size="large"
                  >
                    Take Photo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibrary />}
                    onClick={handlePickFromGallery}
                    fullWidth
                    size="large"
                  >
                    Choose from Gallery
                  </Button>
                  <Divider>
                    <Typography variant="caption" color="text.secondary">
                      or
                    </Typography>
                  </Divider>
                </>
              )}

              {/* File upload - always available */}
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Supports JPG, PNG, GIF, WebP (max 5MB)
              </Typography>
              <Button
                variant={isNative ? 'text' : 'contained'}
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
              >
                {isNative ? 'Upload from Files' : 'Choose File'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileSelect}
                />
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePickerOpen(false)} disabled={imageUploading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};
